import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { MobileBottomNavItem } from '@/components/ui/MobileBottomNav';
import { TabKey } from '@/entities/MobileDocumentTabs';
import { getTabRoute, MOBILE_TABS, TAB_KEY_TO_DOCUMENT_SECTION } from '@/entities/MobileDocumentTabs/config';
import type { KnownSectionSlug } from '@/entities/OrganizationSection';
import { useOrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { navigateToChatWithSession } from '@/utils/chatNavigation';

type UseOrganizationDocumentsBottomNavParams = {
  documentId?: string;
  activeTab: string | null;
  isChatMode: boolean;
  canEditProperties: boolean;
  onTabChange: (key: string) => void;
};

type UseOrganizationDocumentsBottomNavResult = {
  items: MobileBottomNavItem[];
  activeKey: string | null;
  onChange: (key: string) => void;
};

export const useOrganizationDocumentsBottomNav = ({
  documentId,
  activeTab,
  isChatMode,
  canEditProperties,
  onTabChange,
}: UseOrganizationDocumentsBottomNavParams): UseOrganizationDocumentsBottomNavResult => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const sectionsStore = useOrganizationSectionsStore();
  const { selectionStore } = useSidebarDocumentsStore();

  const isChatEnabled = sectionsStore.isSectionEnabled('chat');

  const items = MOBILE_TABS.filter((tab) => {
    if (tab.key === TabKey.chat) {
      return isChatEnabled;
    }

    if (tab.key === TabKey.properties) {
      return canEditProperties;
    }

    const sectionSlug = TAB_KEY_TO_DOCUMENT_SECTION[tab.key as TabKey];

    if (!sectionSlug) {
      return false;
    }

    return sectionsStore.enabledDocumentSections.includes(sectionSlug);
  }).map((tab) => ({
    ...tab,
    label:
      tab.key === TabKey.properties
        ? tab.label
        : (sectionsStore.getSectionName(
            (TAB_KEY_TO_DOCUMENT_SECTION[tab.key as TabKey] ?? 'chat') as KnownSectionSlug,
          ) ?? tab.label),
  }));

  const activeKey = React.useMemo(() => {
    if (isChatMode) {
      return isChatEnabled ? TabKey.chat : null;
    }

    if (!documentId) {
      return null;
    }

    const matched = items.find((item) => {
      if (item.key === TabKey.chat) {
        return false;
      }

      const route = getTabRoute(item.key as TabKey, Number(id), documentId);

      return route ? pathname.includes(route) : false;
    });

    if (matched?.key) {
      return matched.key;
    }

    if (!activeTab) {
      return null;
    }

    return items.some((item) => item.key === activeTab) ? activeTab : null;
  }, [activeTab, documentId, id, isChatEnabled, isChatMode, items, pathname]);

  const onChange = React.useCallback(
    (key: string) => {
      if (key === TabKey.chat) {
        if (!isChatEnabled || !id) {
          return;
        }

        if (isChatMode) {
          return;
        }

        if (documentId) {
          selectionStore.selectDocumentsOnly([documentId]);
          navigateToChatWithSession({ navigate, organizationId: id, documentId });
        } else {
          navigateToChatWithSession({ navigate, organizationId: id });
        }

        return;
      }

      onTabChange(key);
    },
    [documentId, id, isChatEnabled, isChatMode, navigate, onTabChange, selectionStore],
  );

  return { items, activeKey, onChange };
};
